import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  MapPin, Navigation, User, Check, Clock, Car, 
  LogIn, LogOut, ArrowLeft, RefreshCw, AlertCircle
} from 'lucide-react';

const STATUS_CONFIG = {
  on_the_way: { label: 'On The Way', icon: Navigation, color: 'bg-blue-500', textColor: 'text-blue-500' },
  arrived: { label: 'Arrived', icon: MapPin, color: 'bg-gold-500', textColor: 'text-gold-500' },
  pob: { label: 'Passenger On Board', icon: User, color: 'bg-green-500', textColor: 'text-green-500' },
  dop: { label: 'Dropped Off', icon: Check, color: 'bg-purple-500', textColor: 'text-purple-500' },
  check_in: { label: 'Checked In', icon: LogIn, color: 'bg-teal-500', textColor: 'text-teal-500' },
  check_out: { label: 'Checked Out', icon: LogOut, color: 'bg-gray-500', textColor: 'text-gray-500' },
};

export default function DriverEventTracking() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [assignment, setAssignment] = useState(null);
  const [event, setEvent] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [hoursData, setHoursData] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [passengerName, setPassengerName] = useState('');
  const [passengerDestination, setPassengerDestination] = useState('');
  const [locationError, setLocationError] = useState(null);

  const fetchAssignment = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/driver/my-event-assignments`);
      
      const myAssignment = response.data.find(a => a.event_id === eventId);
      if (myAssignment) {
        setAssignment(myAssignment);
        setEvent(myAssignment.event);
      } else {
        toast.error('You are not assigned to this event');
        navigate('/driver/events');
      }
    } catch (error) {
      console.error('Error fetching assignment:', error);
      toast.error('Failed to load assignment');
    }
  }, [eventId, navigate]);

  const fetchActivityLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/driver/events/${eventId}/my-activity`);
      setActivityLogs(response.data);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    }
  }, [eventId]);

  const fetchHoursData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/driver/events/${eventId}/my-hours`);
      setHoursData(response.data);
    } catch (error) {
      console.error('Error fetching hours data:', error);
    }
  }, [eventId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchAssignment();
      await fetchActivityLogs();
      await fetchHoursData();
      setLoading(false);
    };
    loadData();
  }, [fetchAssignment, fetchActivityLogs, fetchHoursData]);

  // Get current location
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      setGettingLocation(true);
      setLocationError(null);
      
      if (!navigator.geolocation) {
        setLocationError('Geolocation is not supported by your browser');
        setGettingLocation(false);
        reject(new Error('Geolocation not supported'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setCurrentLocation(loc);
          setGettingLocation(false);
          resolve(loc);
        },
        (error) => {
          let message = 'Failed to get location';
          if (error.code === 1) message = 'Location access denied. Please enable location services.';
          if (error.code === 2) message = 'Location unavailable';
          if (error.code === 3) message = 'Location request timed out';
          setLocationError(message);
          setGettingLocation(false);
          reject(new Error(message));
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  // Check in
  const handleCheckIn = async () => {
    setActionLoading(true);
    try {
      const location = await getCurrentLocation();
      await axios.post(
        `${API}/driver/events/${eventId}/check-in`,
        { lat: location.lat, lng: location.lng }
      );
      toast.success('Checked in successfully!');
      await fetchAssignment();
      await fetchActivityLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to check in');
    } finally {
      setActionLoading(false);
    }
  };

  // Check out
  const handleCheckOut = async () => {
    setActionLoading(true);
    try {
      const location = await getCurrentLocation();
      await axios.post(
        `${API}/driver/events/${eventId}/check-out`,
        { lat: location.lat, lng: location.lng }
      );
      toast.success('Checked out successfully!');
      await fetchAssignment();
      await fetchActivityLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to check out');
    } finally {
      setActionLoading(false);
    }
  };

  // Update status
  const handleStatusUpdate = async (status) => {
    setActionLoading(true);
    try {
      const location = currentLocation || await getCurrentLocation();
      const response = await axios.post(
        `${API}/driver/events/${eventId}/status`,
        {
          status,
          lat: location.lat,
          lng: location.lng,
          passenger_name: passengerName || null,
          passenger_destination: passengerDestination || null
        }
      );
      
      const statusLabel = STATUS_CONFIG[status]?.label || status;
      
      // Show trip duration if DOP
      if (status === 'dop' && response.data.trip_duration_minutes) {
        const mins = Math.round(response.data.trip_duration_minutes);
        toast.success(`Trip completed! Duration: ${mins} minutes`);
      } else {
        toast.success(`Status: ${statusLabel}`);
      }
      
      // Clear passenger info after DOP
      if (status === 'dop') {
        setPassengerName('');
        setPassengerDestination('');
      }
      
      await fetchAssignment();
      await fetchActivityLogs();
      await fetchHoursData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  // Send location updates periodically when checked in
  useEffect(() => {
    if (assignment?.status === 'checked_in' || assignment?.status === 'active') {
      const sendLocation = async () => {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true, timeout: 5000, maximumAge: 0
            });
          });
          
          await axios.post(
            `${API}/driver/update-location?lat=${position.coords.latitude}&lng=${position.coords.longitude}&event_id=${eventId}`,
            {}
          );
        } catch (error) {
          console.log('Location update failed:', error);
        }
      };
      
      sendLocation();
      const interval = setInterval(sendLocation, 30000); // Every 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [assignment?.status, eventId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!assignment || !event) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Assignment not found</p>
        <Button onClick={() => navigate('/driver/events')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Events
        </Button>
      </div>
    );
  }

  const isCheckedIn = assignment.status === 'checked_in' || assignment.status === 'active';
  const isCheckedOut = assignment.status === 'checked_out';
  const canCheckIn = assignment.status === 'assigned';

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/driver/events')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{event.title}</h1>
          <p className="text-gray-500">{event.venue} • {new Date(event.start_date).toLocaleDateString()}</p>
        </div>
      </div>

      {/* Assignment Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Your Assignment
          </CardTitle>
          <CardDescription>
            {assignment.assigned_location}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-sm text-gray-500">Pickup Location</p>
            <p className="font-medium">{assignment.assigned_location_address}</p>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <p className={`font-semibold capitalize ${
                isCheckedIn ? 'text-green-600' : isCheckedOut ? 'text-gray-500' : 'text-gold-600'
              }`}>
                {assignment.status.replace('_', ' ')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Trips</p>
              <p className="font-semibold">{assignment.total_trips || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Hours Driven</p>
              <p className="font-semibold text-blue-600">
                {hoursData ? `${hoursData.total_driving_hours || 0}h` : '0h'}
              </p>
            </div>
          </div>

          {assignment.check_in_time && (
            <div className="text-sm text-gray-500">
              <Clock className="w-4 h-4 inline mr-1" />
              Checked in: {new Date(assignment.check_in_time).toLocaleTimeString()}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Driving Hours Summary */}
      {hoursData && hoursData.daily_breakdown && hoursData.daily_breakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Driving Hours Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {hoursData.daily_breakdown.map((day, index) => (
                <div key={day.date || index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <p className="font-medium">{new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                    <p className="text-sm text-gray-500">{day.total_trips} trips</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-blue-600">{day.total_hours}h</p>
                    <p className="text-xs text-gray-400">{Math.round(day.total_minutes)} mins</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t flex justify-between items-center">
              <span className="font-medium">Total Event Hours</span>
              <span className="text-2xl font-bold text-blue-600">{hoursData.total_driving_hours}h</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Location Error */}
      {locationError && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-900/20">
          <CardContent className="p-4 flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <span>{locationError}</span>
            <Button size="sm" variant="outline" onClick={getCurrentLocation} className="ml-auto">
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Check In / Check Out */}
      {canCheckIn && (
        <Card className="border-green-200 bg-green-50 dark:bg-green-900/20">
          <CardContent className="p-6 text-center">
            <p className="text-gray-600 mb-4">You must be near the event location to check in</p>
            <Button 
              size="lg" 
              onClick={handleCheckIn} 
              disabled={actionLoading || gettingLocation}
              className="w-full max-w-xs"
            >
              {gettingLocation ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Getting Location...</>
              ) : actionLoading ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Checking In...</>
              ) : (
                <><LogIn className="w-4 h-4 mr-2" /> Check In to Event</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Status Buttons - Only show when checked in */}
      {isCheckedIn && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Car className="w-5 h-5" />
              Update Your Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Optional Passenger Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>
                <Label htmlFor="passenger-name">Passenger Name (optional)</Label>
                <Input
                  id="passenger-name"
                  value={passengerName}
                  onChange={(e) => setPassengerName(e.target.value)}
                  placeholder="Enter passenger name"
                />
              </div>
              <div>
                <Label htmlFor="passenger-dest">Destination (optional)</Label>
                <Input
                  id="passenger-dest"
                  value={passengerDestination}
                  onChange={(e) => setPassengerDestination(e.target.value)}
                  placeholder="Enter destination"
                />
              </div>
            </div>

            {/* Status Buttons */}
            <div className="grid grid-cols-2 gap-4">
              <Button
                size="lg"
                variant="outline"
                className="h-20 flex-col gap-2 border-2 border-blue-200 hover:bg-blue-50 hover:border-blue-400"
                onClick={() => handleStatusUpdate('on_the_way')}
                disabled={actionLoading}
                data-testid="status-on-the-way"
              >
                <Navigation className="w-6 h-6 text-blue-500" />
                <span className="text-blue-700">On The Way</span>
              </Button>
              
              <Button
                size="lg"
                variant="outline"
                className="h-20 flex-col gap-2 border-2 border-gold-200 hover:bg-gold-50 hover:border-gold-400"
                onClick={() => handleStatusUpdate('arrived')}
                disabled={actionLoading}
                data-testid="status-arrived"
              >
                <MapPin className="w-6 h-6 text-gold-500" />
                <span className="text-gold-700">Arrived</span>
              </Button>
              
              <Button
                size="lg"
                variant="outline"
                className="h-20 flex-col gap-2 border-2 border-green-200 hover:bg-green-50 hover:border-green-400"
                onClick={() => handleStatusUpdate('pob')}
                disabled={actionLoading}
                data-testid="status-pob"
              >
                <User className="w-6 h-6 text-green-500" />
                <span className="text-green-700">POB</span>
                <span className="text-xs text-gray-500">Passenger On Board</span>
              </Button>
              
              <Button
                size="lg"
                variant="outline"
                className="h-20 flex-col gap-2 border-2 border-purple-200 hover:bg-purple-50 hover:border-purple-400"
                onClick={() => handleStatusUpdate('dop')}
                disabled={actionLoading}
                data-testid="status-dop"
              >
                <Check className="w-6 h-6 text-purple-500" />
                <span className="text-purple-700">DOP</span>
                <span className="text-xs text-gray-500">Dropped Off</span>
              </Button>
            </div>

            {/* Check Out Button */}
            <div className="pt-4 border-t">
              <Button
                variant="outline"
                className="w-full border-red-200 text-red-600 hover:bg-red-50"
                onClick={handleCheckOut}
                disabled={actionLoading}
                data-testid="check-out-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Check Out from Event
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Checked Out State */}
      {isCheckedOut && (
        <Card className="border-gray-200 bg-gray-50">
          <CardContent className="p-6 text-center">
            <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold">Event Completed</h3>
            <p className="text-gray-500">You have checked out from this event</p>
            <p className="text-lg font-bold mt-2">Total Trips: {assignment.total_trips || 0}</p>
          </CardContent>
        </Card>
      )}

      {/* Activity Log */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Activity Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activityLogs.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No activity yet</p>
          ) : (
            <div className="space-y-3">
              {activityLogs.map((log, index) => {
                const config = STATUS_CONFIG[log.status] || { label: log.status, color: 'bg-gray-500' };
                const IconComponent = config.icon || Clock;
                return (
                  <div key={log.id || index} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className={`p-2 rounded-full ${config.color} text-white`}>
                      <IconComponent className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{config.label}</p>
                      {log.passenger_name && (
                        <p className="text-sm text-gray-500">Passenger: {log.passenger_name}</p>
                      )}
                      {log.passenger_destination && (
                        <p className="text-sm text-gray-500">To: {log.passenger_destination}</p>
                      )}
                      {log.trip_number && log.status !== 'check_in' && log.status !== 'check_out' && (
                        <p className="text-xs text-gray-400">Trip #{log.trip_number}</p>
                      )}
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
